import { apiClient } from "../axiosInstance";

class ProductService {
  constructor() {
    this.baseUrl = "/products";
  }

  // ==================== PUBLIC ENDPOINTS ====================

  /**
   * Get all products with pagination and filters
   * @param {Object} params - Query parameters
   */
  async getAllProducts(params = {}) {
    try {
      const response = await apiClient.get(this.baseUrl, { params });
      return response.data;
    } catch (error) {
      console.error("Get all products error:", error);
      throw error;
    }
  }

  /**
   * Get featured products
   * @param {number} limit - Number of products to return (max 50)
   */
  async getFeaturedProducts(limit = 10) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/featured`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error("Get featured products error:", error);
      throw error;
    }
  }

  /**
   * Get new products
   * @param {number} limit - Number of products to return (max 50)
   */
  async getNewProducts(limit = 10) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/new`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error("Get new products error:", error);
      throw error;
    }
  }

  /**
   * Get popular products
   * @param {number} limit - Number of products to return (max 50)
   */
  async getPopularProducts(limit = 10) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/popular`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error("Get popular products error:", error);
      throw error;
    }
  }

  /**
   * Get top viewed products
   * @param {number} limit - Number of products to return (max 50)
   */
  async getTopViewedProducts(limit = 10) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/top-viewed`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error("Get top viewed products error:", error);
      throw error;
    }
  }

  /**
   * Get products by category
   * @param {string} category - Product category (food, non-food)
   */
  async getProductsByCategory(category) {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/category/${category}`,
      );
      return response.data;
    } catch (error) {
      console.error("Get products by category error:", error);
      throw error;
    }
  }

  /**
   * Quick search products
   * @param {string} query - Search query
   * @param {number} limit - Number of results to return (max 50)
   */
  async quickSearch(query, limit = 10) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/search/quick`, {
        params: { q: query, limit },
      });
      return response.data;
    } catch (error) {
      console.error("Quick search error:", error);
      throw error;
    }
  }

  /**
   * Advanced search products
   * @param {Object} searchParams - Search parameters
   */
  async advancedSearch(searchParams) {
    try {
      const response = await apiClient.post(
        `${this.baseUrl}/search/advanced`,
        searchParams,
      );
      return response.data;
    } catch (error) {
      console.error("Advanced search error:", error);
      throw error;
    }
  }

  /**
   * Get product gallery images
   * @param {number} id - Product ID
   */
  async getProductGallery(id) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${id}/gallery`);
      return response.data;
    } catch (error) {
      console.error("Get product gallery error:", error);
      throw error;
    }
  }

  /**
   * Get product main image
   * @param {number} id - Product ID
   */
  async getProductMainImage(id) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${id}/main-image`);
      return response.data;
    } catch (error) {
      console.error("Get product main image error:", error);
      throw error;
    }
  }

  /**
   * Get product by ID
   * @param {number} id - Product ID
   */
  async getProductById(id) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error("Get product by id error:", error);
      throw error;
    }
  }

  /**
   * Get product by slug
   * @param {string} slug - Product slug
   */
  async getProductBySlug(slug) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/slug/${slug}`);
      return response.data;
    } catch (error) {
      console.error("Get product by slug error:", error);
      throw error;
    }
  }

  /**
   * Get product by SKU
   * @param {string} sku - Product SKU
   */
  async getProductBySku(sku) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/sku/${sku}`);
      return response.data;
    } catch (error) {
      console.error("Get product by sku error:", error);
      throw error;
    }
  }

  /**
   * Get product by UUID
   * @param {string} uuid - Product UUID
   */
  async getProductByUuid(uuid) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/uuid/${uuid}`);
      return response.data;
    } catch (error) {
      console.error("Get product by uuid error:", error);
      throw error;
    }
  }

  /**
   * Get related products
   * @param {number} id - Product ID
   * @param {number} limit - Number of related products to return (max 20)
   */
  async getRelatedProducts(id, limit = 5) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${id}/related`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error("Get related products error:", error);
      throw error;
    }
  }

  // ==================== ADMIN ENDPOINTS ====================

  /**
   * Create a new product with image upload (Admin only)
   * @param {Object} productData - Product data
   * @param {File} mainImage - Main product image file (optional)
   * @param {File[]} galleryImages - Gallery image files (optional)
   */
  async createProduct(productData, mainImage = null, galleryImages = []) {
    try {
      const hasFiles = mainImage || (galleryImages && galleryImages.length > 0);
      let response;

      if (hasFiles) {
        const formData = new FormData();

        // Append each field individually
        Object.keys(productData).forEach((key) => {
          const value = productData[key];
          if (value === undefined || value === null) return;

          if (key === "specifications" || key === "features") {
            formData.append(key, JSON.stringify(value));
          } else if (typeof value === "boolean") {
            formData.append(key, value.toString());
          } else {
            formData.append(key, value);
          }
        });

        if (mainImage) formData.append("mainImage", mainImage);
        if (galleryImages && galleryImages.length > 0) {
          galleryImages.forEach((file) =>
            formData.append("galleryImages", file),
          );
        }

        response = await apiClient.post(this.baseUrl, formData);
      } else {
        // No files – send as JSON
        response = await apiClient.post(this.baseUrl, productData);
      }

      return response.data;
    } catch (error) {
      console.error("Create product error:", error);
      throw error;
    }
  }

  /**
   * Update product with image upload (Admin only)
   * @param {number} id - Product ID
   * @param {Object} productData - Product data to update
   * @param {File} mainImage - Main product image file (optional)
   * @param {File[]} galleryImages - Gallery image files (optional)
   */
  async updateProduct(id, productData, mainImage = null, galleryImages = []) {
    try {
      const hasFiles = mainImage || (galleryImages && galleryImages.length > 0);
      let response;

      if (hasFiles) {
        const formData = new FormData();

        Object.keys(productData).forEach((key) => {
          const value = productData[key];
          if (value === undefined || value === null) return;

          if (key === "specifications" || key === "features") {
            formData.append(key, JSON.stringify(value));
          } else if (typeof value === "boolean") {
            formData.append(key, value.toString());
          } else {
            formData.append(key, value);
          }
        });

        if (mainImage) formData.append("mainImage", mainImage);
        if (galleryImages && galleryImages.length > 0) {
          galleryImages.forEach((file) =>
            formData.append("galleryImages", file),
          );
        }

        response = await apiClient.patch(`${this.baseUrl}/${id}`, formData);
      } else {
        response = await apiClient.patch(`${this.baseUrl}/${id}`, productData);
      }

      return response.data;
    } catch (error) {
      console.error("Update product error:", error);
      throw error;
    }
  }

  /**
   * Update product main image (Admin only)
   * @param {number} id - Product ID
   * @param {File} image - Image file
   */
  async updateMainImage(id, image) {
    try {
      const formData = new FormData();
      formData.append("image", image);

      const response = await apiClient.post(
        `${this.baseUrl}/${id}/main-image`,
        formData,
      );
      return response.data;
    } catch (error) {
      console.error("Update main image error:", error);
      throw error;
    }
  }

  /**
   * Update product main image from base64 (Admin only)
   * @param {number} id - Product ID
   * @param {string} base64Data - Base64 encoded image data
   * @param {string} mimeType - Image MIME type
   * @param {string} alt - Optional alt text
   */
  async updateMainImageFromBase64(id, base64Data, mimeType, alt = null) {
    try {
      const response = await apiClient.post(
        `${this.baseUrl}/${id}/main-image-base64`,
        {
          base64Data,
          mimeType,
          alt,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Update main image from base64 error:", error);
      throw error;
    }
  }

  /**
   * Add gallery images (Admin only)
   * @param {number} id - Product ID
   * @param {File[]} images - Image files
   */
  async addGalleryImages(id, images) {
    try {
      const formData = new FormData();
      images.forEach((image) => {
        formData.append("images", image);
      });

      const response = await apiClient.post(
        `${this.baseUrl}/${id}/gallery`,
        formData,
      );
      return response.data;
    } catch (error) {
      console.error("Add gallery images error:", error);
      throw error;
    }
  }

  /**
   * Add gallery image from base64 (Admin only)
   * @param {number} id - Product ID
   * @param {string} base64Data - Base64 encoded image data
   * @param {string} mimeType - Image MIME type
   * @param {string} filename - Filename
   * @param {string} originalName - Original name
   * @param {string} alt - Optional alt text
   */
  async addGalleryImageFromBase64(
    id,
    base64Data,
    mimeType,
    filename,
    originalName,
    alt = null,
  ) {
    try {
      const response = await apiClient.post(
        `${this.baseUrl}/${id}/gallery-base64`,
        {
          base64Data,
          mimeType,
          filename,
          originalName,
          alt,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Add gallery image from base64 error:", error);
      throw error;
    }
  }

  /**
   * Remove gallery image (Admin only)
   * @param {number} productId - Product ID
   * @param {string} imageId - Image ID
   */
  async removeGalleryImage(productId, imageId) {
    try {
      const response = await apiClient.delete(
        `${this.baseUrl}/${productId}/gallery/${imageId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Remove gallery image error:", error);
      throw error;
    }
  }

  /**
   * Reorder gallery images (Admin only)
   * @param {number} id - Product ID
   * @param {Array} imageOrders - Array of { id, order } objects
   */
  async reorderGalleryImages(id, imageOrders) {
    try {
      const response = await apiClient.patch(
        `${this.baseUrl}/${id}/gallery/reorder`,
        {
          imageOrders,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Reorder gallery images error:", error);
      throw error;
    }
  }

  /**
   * Update gallery image alt text (Admin only)
   * @param {number} productId - Product ID
   * @param {string} imageId - Image ID
   * @param {string} alt - Alt text
   */
  async updateGalleryImageAlt(productId, imageId, alt) {
    try {
      const response = await apiClient.patch(
        `${this.baseUrl}/${productId}/gallery/${imageId}/alt`,
        {
          alt,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Update gallery image alt error:", error);
      throw error;
    }
  }

  /**
   * Clear all gallery images (Admin only)
   * @param {number} id - Product ID
   */
  async clearGalleryImages(id) {
    try {
      const response = await apiClient.delete(`${this.baseUrl}/${id}/gallery`);
      return response.data;
    } catch (error) {
      console.error("Clear gallery images error:", error);
      throw error;
    }
  }

  /**
   * Delete product (soft delete) (Admin only)
   * @param {number} id - Product ID
   */
  async deleteProduct(id) {
    try {
      const response = await apiClient.delete(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error("Delete product error:", error);
      throw error;
    }
  }

  /**
   * Duplicate product (Admin only)
   * @param {number} id - Product ID to duplicate
   */
  async duplicateProduct(id) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/${id}/duplicate`);
      return response.data;
    } catch (error) {
      console.error("Duplicate product error:", error);
      throw error;
    }
  }

  /**
   * Toggle product featured status (Admin only)
   * @param {number} id - Product ID
   * @param {boolean} isFeatured - Featured status
   */
  async toggleFeatured(id, isFeatured) {
    try {
      const response = await apiClient.patch(
        `${this.baseUrl}/${id}/toggle-featured`,
        {
          isFeatured,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Toggle featured error:", error);
      throw error;
    }
  }

  /**
   * Toggle product new status (Admin only)
   * @param {number} id - Product ID
   * @param {boolean} isNew - New status
   */
  async toggleNew(id, isNew) {
    try {
      const response = await apiClient.patch(
        `${this.baseUrl}/${id}/toggle-new`,
        {
          isNew,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Toggle new error:", error);
      throw error;
    }
  }

  /**
   * Toggle product availability (Admin only)
   * @param {number} id - Product ID
   * @param {boolean} isAvailable - Availability status
   */
  async toggleAvailability(id, isAvailable) {
    try {
      const response = await apiClient.patch(
        `${this.baseUrl}/${id}/toggle-availability`,
        {
          isAvailable,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Toggle availability error:", error);
      throw error;
    }
  }

  /**
   * Toggle product active status (Admin only)
   * @param {number} id - Product ID
   * @param {boolean} isActive - Active status
   */
  async toggleActive(id, isActive) {
    try {
      const response = await apiClient.patch(
        `${this.baseUrl}/${id}/toggle-active`,
        {
          isActive,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Toggle active error:", error);
      throw error;
    }
  }

  /**
   * Bulk delete products (Admin only)
   * @param {number[]} ids - Array of product IDs
   */
  async bulkDeleteProducts(ids) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/bulk-delete`, {
        ids,
      });
      return response.data;
    } catch (error) {
      console.error("Bulk delete products error:", error);
      throw error;
    }
  }

  /**
   * Bulk update product status (Admin only)
   * @param {number[]} ids - Array of product IDs
   * @param {boolean} isActive - Active status
   */
  async bulkUpdateStatus(ids, isActive) {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/bulk-status`, {
        ids,
        isActive,
      });
      return response.data;
    } catch (error) {
      console.error("Bulk update status error:", error);
      throw error;
    }
  }

  /**
   * Bulk update featured status (Admin only)
   * @param {number[]} ids - Array of product IDs
   * @param {boolean} isFeatured - Featured status
   */
  async bulkUpdateFeatured(ids, isFeatured) {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/bulk-featured`, {
        ids,
        isFeatured,
      });
      return response.data;
    } catch (error) {
      console.error("Bulk update featured error:", error);
      throw error;
    }
  }

  /**
   * Bulk update new status (Admin only)
   * @param {number[]} ids - Array of product IDs
   * @param {boolean} isNew - New status
   */
  async bulkUpdateNew(ids, isNew) {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/bulk-new`, {
        ids,
        isNew,
      });
      return response.data;
    } catch (error) {
      console.error("Bulk update new error:", error);
      throw error;
    }
  }

  /**
   * Bulk update availability (Admin only)
   * @param {number[]} ids - Array of product IDs
   * @param {boolean} isAvailable - Availability status
   */
  async bulkUpdateAvailability(ids, isAvailable) {
    try {
      const response = await apiClient.patch(
        `${this.baseUrl}/bulk-availability`,
        {
          ids,
          isAvailable,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Bulk update availability error:", error);
      throw error;
    }
  }

  /**
   * Get product statistics (Admin only)
   */
  async getProductStats() {
    try {
      // Use the same base URL - the interceptor will handle auth based on URL pattern
      const response = await apiClient.get(`${this.baseUrl}/stats`);
      return response.data;
    } catch (error) {
      console.error("Get product stats error:", error);
      throw error;
    }
  }

  /**
   * Get category distribution (Admin only)
   */
  async getCategoryDistribution() {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/category-distribution`,
      );
      return response.data;
    } catch (error) {
      console.error("Get category distribution error:", error);
      throw error;
    }
  }

  /**
   * Get frequently requested products (Admin only)
   * @param {number} limit - Number of products to return (max 50)
   */
  async getFrequentlyRequestedProducts(limit = 10) {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/frequently-requested`,
        {
          params: { limit },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Get frequently requested products error:", error);
      throw error;
    }
  }

  /**
   * Get unpopular products (Admin only)
   */
  async getUnpopularProducts() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/unpopular`);
      return response.data;
    } catch (error) {
      console.error("Get unpopular products error:", error);
      throw error;
    }
  }

  /**
   * Get products by date range (Admin only)
   * @param {string} startDate - Start date (ISO 8601)
   * @param {string} endDate - End date (ISO 8601)
   */
  async getProductsByDateRange(startDate, endDate) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/date-range`, {
        params: { startDate, endDate },
      });
      return response.data;
    } catch (error) {
      console.error("Get products by date range error:", error);
      throw error;
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Get category badge color
   * @param {string} category - Product category
   */
  getCategoryBadge(category) {
    const badges = {
      food: {
        color:
          "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400",
        icon: "ShoppingBag",
      },
      "non-food": {
        color:
          "bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400",
        icon: "Package",
      },
    };
    return badges[category] || badges.food;
  }

  /**
   * Get category label
   * @param {string} category - Product category
   */
  getCategoryLabel(category) {
    const labels = {
      food: "Food",
      "non-food": "Non-Food",
    };
    return labels[category] || category;
  }

  /**
   * Generate slug from name
   * @param {string} name - Product name
   */
  generateSlug(name) {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }
}

// Create and export a single instance
export const productService = new ProductService();
